import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "hooks/useSelectedLanguage";

import { StatusTag, type StatusTagProps } from "components/StatusTag/StatusTag";

import type { InviteBundleStatusTagProps } from "./InviteBundleStatusTag.model";

export const InviteBundleStatusTag: React.FC<InviteBundleStatusTagProps> = ({
  dataCy,
  maxUsage,
  currentUsage,
  expirationDate,
}) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation("teacher-dashboard");

  // Determine icon, color and label to display based on current status
  const bundleStatusTagProps: StatusTagProps = useMemo(() => {
    // Full
    if (currentUsage === maxUsage) {
      return {
        icon: "far fa-check-double",
        label: t("studentLicense.invitationBundleList.status.full"),
        variant: "default",
      };
    }

    // Expired
    if (new Date(expirationDate) <= new Date()) {
      return {
        icon: "fas fa-times-circle",
        label: t("studentLicense.invitationBundleList.status.expired"),
        variant: "error",
      };
    }

    // Active
    return {
      icon: "far fa-check",
      label: t("studentLicense.invitationBundleList.status.active"),
      variant: "success",
    };
  }, [currentLanguage, currentUsage, maxUsage, expirationDate]);

  return (
    <StatusTag
      dataCy={dataCy}
      label={bundleStatusTagProps.label}
      icon={bundleStatusTagProps.icon}
      variant={bundleStatusTagProps.variant}
    />
  );
};
